import {
  formatDate as formatDateFeCommon,
  formatIncludesTime as formatIncludesTimeFeCommon,
  isValidDate,
  toISODatetime as toISODatetimeFeCommon,
  type DateFormatName,
  type UseFormattedDateOptions,
} from '@lastbrand/fe-common'
import { useMemo } from 'react'

import { useLocaleContext } from '@src/components/locale/locale.context'
import { IS_DEV_ENV, IS_PROD_BUILD } from '@src/config/env'
import { TAbsoluteLocale } from '@src/config/localisation/locale-config.types'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import type { DateFormatOverrides } from './date-time.constants'

/** Local result type for hook return to satisfy strict lint (fe-common types may resolve as any). */
interface FormattedDateResultLocal {
  formatted: string
  iso: string
}

/** Typed wrappers for fe-common functions (resolves strict lint when fe-common types are opaque). */
const formatDateFn = formatDateFeCommon as (
  date: Date | number | string,
  formatName: DateFormatName,
  locale: string,
  options?: UseFormattedDateOptions,
) => string
const formatIncludesTimeFn = formatIncludesTimeFeCommon as (formatName: DateFormatName) => boolean
const toISODatetimeFn = toISODatetimeFeCommon as (date: Date | number | string, includeTime?: boolean) => string

/**
 * Handle an invalid date by throwing in local dev, capturing to Datadog
 * in non-prod deployed environments, and logging a warning otherwise.
 */
function handleInvalidDate(date: Date | number | string, context?: Record<string, unknown>): void {
  const error = new Error(`[DateTime] Invalid date value: ${String(date)}`)

  if (IS_DEV_ENV) {
    throw error
  }

  if (!IS_PROD_BUILD) {
    captureException(error, {
      module: MODULES.UNKNOWN,
      feature: FEATURES.UNKNOWN,
      extraInfo: { date: String(date), ...context },
    })
  }
}

/**
 * Re-export fe-common formatDate for consumers that import from the block.
 *
 * @param date - The date to format (Date object, timestamp, or date string)
 * @param formatName - The semantic format name
 * @param locale - The locale to use for formatting
 * @param options - Optional formatting options
 * @returns The formatted date string
 */
export function formatDate(
  date: Date | number | string,
  formatName: DateFormatName,
  locale: TAbsoluteLocale,
  options: UseFormattedDateOptions = {},
): string {
  return formatDateFn(date, formatName, locale, options)
}

/**
 * Re-export fe-common utilities for consumers that import from the block.
 */
export { isValidDate, formatIncludesTime, toISODatetime } from '@lastbrand/fe-common'
export { formatDateTime } from '@lastbrand/fe-common'

/**
 * Hook for formatting a date using the Intl Web API with locale awareness.
 * Returns both the formatted date string and ISO datetime string.
 *
 * @param date - The date to format (Date object, timestamp, or date string)
 * @param formatName - The semantic format name
 * @param options - Optional formatting options
 * @returns Object containing formatted date and ISO datetime string
 *
 * @example
 * ```tsx
 * // Format a full date
 * const { formatted, iso } = useFormattedDate(new Date(), 'NUMERIC_SLASH_DATE')
 * // formatted: "01/08/2026"
 * // iso: "2026-01-08"
 *
 * // Format with time
 * const { formatted, iso } = useFormattedDate(new Date(), 'SHORT_MONTH_DAY_AND_TIME_12H')
 * // formatted: "Jan 08 3:45 PM"
 * // iso: "2026-01-08T15:45:00.000Z"
 * ```
 */
interface UseFormattedDateHookOptions {
  overrides?: DateFormatOverrides
}

export function useFormattedDate(
  date: Date | number | string,
  formatName: DateFormatName,
  options: UseFormattedDateHookOptions = {},
): FormattedDateResultLocal | null {
  const { locale } = useLocaleContext()

  return useMemo(() => {
    if (!isValidDate(date)) {
      handleInvalidDate(date, { formatName })
      return null
    }

    const formatted = formatDateFn(date, formatName, locale, options)
    const includeTime = formatIncludesTimeFn(formatName)
    const iso = toISODatetimeFn(date, includeTime)
    return { formatted, iso }
  }, [date, formatName, locale, options])
}
